<script>
    import Form from '@form/Form';
    import Container from '@services/Container';
    import '@desktop/scss/components/invoicing-modality-mixin.scss'

    const container = Container.getInstance();
    const flashMessenger = container.getFlashMessenger();

    export default {
        data() {
            return {
                /** options for invoice recipient select fields */
                invoiceRecipientOptions: [],
                loaded: false,
                /** existing modality entries for modality owner */
                modalityData: {},
                modalityForm: new Form({}),
                /** allowed modality entries and table structures */
                modalityStructure: {},
                carDealerModalityTableFields: [
                    {
                        key: 'invoicingModalityArticleName',
                        label: 'Zuordnung',
                        thClass: 'invoicing-modality-column-car-dealer-article',
                    },
                    {
                        key: 'fee',
                        label: 'Gebührenhöhe',
                        thClass: 'invoicing-modality-column-narrow',
                    },
                    {
                        key: 'invoiceRecipient',
                        label: 'Berechnung an',
                        thClass: 'invoicing-modality-column-wide',
                        tdClass: 'allow-y-overflow',
                    },
                    {
                        key: 'invoiceRecipientAddressNo',
                        label: 'Abweichende Adressnummer',
                        thClass: 'invoicing-modality-column-narrow',
                    },
                ],
                serviceProviderModalityTableFields: [
                    {
                        key: 'invoicingModalityArticleName',
                        label: 'Zuordnung',
                        thClass: 'invoicing-modality-column-narrow',
                    },
                    {
                        key: 'serviceRecipientFirmName',
                        label: 'Leistungsempfänger',
                        thClass: 'invoicing-modality-column-wide',
                    },
                    {
                        key: 'fee',
                        label: 'Gebührenhöhe',
                        thClass: 'invoicing-modality-column-narrow',
                    },
                    {
                        key: 'invoiceRecipient',
                        label: 'Berechnung an',
                        thClass: 'invoicing-modality-column-wide',
                        tdClass: 'allow-y-overflow',
                    },
                    {
                        key: 'invoiceRecipientAddressNo',
                        label: 'Abweichende Adressnummer',
                        thClass: 'invoicing-modality-column-narrow',
                    },
                ],
            }
        },
        computed: {
            appraiserForm() {
                return this.modalityForm.getSubform('appraiser');
            },
            carDealerForm() {
                return this.modalityForm.getSubform('carDealer');
            },
            serviceProviderForm() {
                return this.modalityForm.getSubform('serviceProvider');
            },
        },
        methods: {
            /** build the table form data for the provided table structure, assigning relevant modality entry data to
             * matching table row form objects */
            buildTableFormData(tableStructure) {
                const data = this.modalityData;
                const tableRowsFormData = {};
                let rowCounter = 0;

                // the attributes of structureEntry originate from InvoicingModalityEntryStructureTransformer
                tableStructure.forEach(structureEntry => {
                    // will define the attributes of a single table row sub form object and thus the contents of its
                    // associated table row in the view
                    const tableRowAttributes = {
                        invoicingModalityEntryId: null,
                        invoicingModalityArticleId: structureEntry.attributes.articleId,
                        invoicingModalityArticleName: structureEntry.attributes.articleName,
                        serviceRecipientFirmId: structureEntry.attributes.serviceRecipientFirmId,
                        serviceRecipientFirmGroupId: structureEntry.attributes.serviceRecipientFirmGroupId,
                        serviceRecipientFirmName: structureEntry.attributes.serviceRecipientFirmName,
                        fee: null,
                        invoiceRecipient: null,
                        invoiceRecipientAddressNo: null,
                        isRemnant: structureEntry.attributes.isRemnant,
                    };

                    // find the matching modality entry for the current row if it exists and assign its data
                    // the attributes of dataEntry originate from InvoicingModalityEntryDataTransformer
                    data.some(dataEntry => {
                        if (dataEntry.attributes.articleId === tableRowAttributes.invoicingModalityArticleId
                            && dataEntry.attributes.serviceRecipientFirmId === tableRowAttributes.serviceRecipientFirmId
                            && dataEntry.attributes.serviceRecipientFirmGroupId
                                === tableRowAttributes.serviceRecipientFirmGroupId
                        ) {
                            tableRowAttributes.invoicingModalityEntryId = dataEntry.attributes.invoicingModalityEntryId;
                            tableRowAttributes.fee = dataEntry.attributes.fee;
                            tableRowAttributes.invoiceRecipient = dataEntry.attributes.invoiceRecipient;
                            tableRowAttributes.invoiceRecipientAddressNo
                                = dataEntry.attributes.invoiceRecipientAddressNo;

                            return true;
                        }
                    });

                    // index with string key instead of int key in order to prevent reindexing later on
                    tableRowsFormData[rowCounter] = {data: {attributes: tableRowAttributes}};
                    rowCounter++;
                });

                return {data: {attributes: tableRowsFormData}};
            },
            handleSubmitError(error) {
                if (this.modalityForm.setValidationErrors(error)) {
                    return;
                }
                flashMessenger.addError('Fehler beim Speichern der Abrechnungsmodalitäten: ' + error.getStatusText())
            },
            initializeTables() {
                // prepare form data for table sub forms
                const serviceProviderFormData = this.buildTableFormData(
                    this.modalityStructure.serviceProviderModalityEntries.data
                );
                const carDealerFormData = this.buildTableFormData(this.modalityStructure.carDealerModalityEntries.data);
                const appraiserFormData = this.buildTableFormData(this.modalityStructure.appraiserModalityEntries.data);

                // update modality form by defining table sub form objects and cause the rendering of the modality
                // tables in the view
                this.modalityForm.setData({
                    serviceProvider: serviceProviderFormData,
                    carDealer: carDealerFormData,
                    appraiser: appraiserFormData,
                });
            },
            /** return a filtered deep copy of the modality form data that does not contain table row sub form objects
             * of empty table rows */
            prepareDataForSubmit() {
                const filteredModalityForm = {};

                const modalityFormAttributes = this.modalityForm.getData();
                // iterate over table form objects
                Object.keys(modalityFormAttributes).forEach((tableFormKey) => {
                    const filteredTableForm = {data: {attributes: {}}};

                    const tableFormAttributes = modalityFormAttributes[tableFormKey].data.attributes;
                    // iterate over table row form objects
                    Object.keys(tableFormAttributes).forEach((rowIndex) => {
                        const rowFormAttributes = tableFormAttributes[rowIndex].data.attributes;

                        if (rowFormAttributes.fee === null
                            && rowFormAttributes.invoiceRecipient === null
                            && rowFormAttributes.invoiceRecipientAddressNo === null
                        ) {
                            // do not include current row in filtered form data because it is empty
                            return;
                        }

                        // assign row form to filtered table form
                        filteredTableForm.data.attributes[rowIndex] = tableFormAttributes[rowIndex];
                    })

                    // assign filtered table form to filtered modality form
                    filteredModalityForm[tableFormKey] = filteredTableForm;
                });

                return filteredModalityForm;
            },
            resetCommonData() {
                this.invoiceRecipientOptions = [];
                this.modalityData = {};
                this.modalityForm = new Form({});
                this.modalityStructure = {};
            },
        },
    }
</script>
